import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Modal, { ModalHeader, ModalTitle, ModalContent, ModalActions } from '../components/Modal';
import { Button } from '../components/Button';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import Ai from '../store/models/Ai';
import classNames from 'classnames';

const cx = classNames;

export const ViewDocumentModal = ({ onRequestClose, document, projectId, chatbotId }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [content, setContent] = useState('');
    const [editedContent, setEditedContent] = useState('');

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                const result = await dispatch(Ai.actions.fetchChatbotDocument(chatbotId, projectId, document.id));
                let processedContent = result.content;
                
                // If it's a CSV file and we have column metadata, add column names as header
                if (document.metadata.type === 'file' && 
                    document.metadata.filename?.endsWith('.csv') && 
                    document.metadata.sql_schema?.columns) {
                    const columnNames = document.metadata.sql_schema.columns.map(col => col.name).join(',');
                    processedContent = `${columnNames}\n${processedContent}`;
                }
                
                setContent(processedContent);
                setEditedContent(processedContent);
            } catch (error) {
                toast.error('Failed to load document');
            } finally {
                setLoading(false);
            }
        };

        fetchDocument();
    }, [dispatch, projectId, chatbotId, document.id]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const file = new File([editedContent], document.metadata.filename, { type: 'application/json' });
            const formData = new FormData();
            formData.append('file', file);
            formData.append('project_id', projectId);

            formData.append('metadata', JSON.stringify({
                type: 'file',
                ...document.metadata
            }));

            await dispatch(Ai.actions.updateChatbotDocument(chatbotId, document.id, formData, true));
            setContent(editedContent);
            toast.success('Document updated successfully');
        } catch (error) {
            toast.error('Failed to update document');
        } finally {
            setLoading(false);
        }
    };

    const hasChanges = editedContent !== content;

    return (
        <Modal className={cx("ViewDocumentModal")} onRequestClose={onRequestClose}>
            <ModalHeader>
                <ModalTitle>{document.metadata.filename || 'Document'}</ModalTitle>
            </ModalHeader>

            <ModalContent>
                {loading ? (
                    <div className="ViewDocumentModal__loader">
                        <Loader size="large" color="grey" />
                    </div>
                ) : (
                    <div className="ViewDocumentModal__content">
                        <textarea
                            className="ViewDocumentModal__editor"
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            style={{ height: '60vh' }}
                        />
                    </div>
                )}
            </ModalContent>

            <ModalActions>
                <Button
                    color="green"
                    onClick={handleSave}
                    title="Save"
                    loading={loading}
                    disabled={!hasChanges}
                />
                <Button color="transparent" onClick={onRequestClose} title="Close" />
            </ModalActions>
        </Modal>
    );
}; 